<template>
	<b-modal ref="modal" hide-footer size="lg" @hidden="onHidden">
		<template v-slot:modal-title>{{ $t('invoice.sepa_transfer.titre') }}</template>

		<LoadingSpinner v-if="loading" />

		<template v-else-if="step === 'check'">
			<b-alert v-if="skipped_no_iban.length > 0" show variant="warning">
				{{ $t('invoice.sepa_transfer.avertissement_iban') }}
				<ul class="mb-0 mt-2">
					<li v-for="tiers in skipped_no_iban" :key="tiers.tiers_id">
						<router-link :to="{ name: 'tiersRibList', params: { tiers_id: tiers.tiers_id } }">
							{{ tiers.tiers_rs }}
						</router-link>
						<span class="ml-1">({{ tiers.invoices.join(', ') }})</span>
					</li>
				</ul>
			</b-alert>

			<b-alert v-if="skipped_iban.length > 0" show variant="danger">
				{{ $t('invoice.sepa_transfer.avertissement_iban_non_approuve') }}
				<ul class="list-unstyled mb-0">
					<li v-for="invoice in skipped_iban" :key="invoice.suppliersocr_id" class="mt-3">
						<b>{{ invoice.suppliersocr_num }}</b> — {{ invoice.tiers_rs }}
						<p class="mb-1">{{ $t('invoice.sepa_transfer.skip.' + invoice.reason) }}</p>

						<!-- Le compte que la facture annonce s'approuve d'ici : c'est la décision de
						     l'écran de contrôle, prise là où son absence se fait sentir. -->
						<div v-if="invoice.suppliersocr_seller_iban" class="row align-items-center mb-2">
							<div class="col-12 col-md-7">
								{{ $t('invoice.iban_control.iban_facture') }} : {{ invoice.suppliersocr_seller_iban }}
							</div>

							<div class="col-12 col-md-5 mt-2 mt-md-0">
								<button class="btn btn-primary btn-rounded" :disabled="processing" @click="onApproveIban(invoice)">
									{{ $t('invoice.iban_control.approuver') }}
									<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse/>
									<font-awesome-icon v-else :icon="['fal', 'check']"/>
								</button>
							</div>
						</div>

						<!-- Comptes de la fiche laissés en quarantaine : les approuver depuis ici évite
						     un aller-retour vers le tiers pour un seul clic. -->
						<div v-for="rib in quarantinedRibs(invoice)" :key="rib.tiersrib_id" class="row align-items-center mb-2">
							<div class="col-12 col-md-7">{{ rib.tiersrib_content }}</div>

							<div class="col-12 col-md-5 mt-2 mt-md-0">
								<button class="btn btn-primary btn-rounded" :disabled="processing" @click="onApproveRib(invoice, rib)">
									{{ $t('invoice.iban_control.approuver') }}
									<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse/>
									<font-awesome-icon v-else :icon="['fal', 'check']"/>
								</button>
							</div>
						</div>

						<div class="row">
							<div v-if="approvedRibs(invoice).length > 0" class="col-12 col-md-7">
								<e-select
									:options="approvedRibs(invoice)"
									track-by="tiersrib_id"
									label="tiersrib_content"
									:placeholder="$t('invoice.sepa_transfer.choisir_iban_approuve')"
									:allow-empty="false"
									:disabled="processing"
									@input="onChooseRib(invoice, $event)"
								/>
							</div>
						</div>
					</li>
				</ul>
			</b-alert>

			<b-alert v-if="skipped_not_approved.length > 0" show variant="warning">
				{{ $t('invoice.sepa_transfer.avertissement_non_approuve') }}
				<ul class="mb-0 mt-2">
					<li v-for="invoice in skipped_not_approved" :key="invoice.suppliersocr_id">
						{{ invoice.suppliersocr_num }} — {{ invoice.tiers_rs }}
					</li>
				</ul>
				<button class="btn btn-primary btn-rounded mt-3" :disabled="processing" @click="onApprove">
					{{ $t('invoice.sepa_transfer.approuver_pour_paiement') }}
					<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse/>
					<font-awesome-icon v-else :icon="['fal', 'check']"/>
				</button>
			</b-alert>

			<b-alert v-if="skipped_other.length > 0" show variant="warning">
				<ul class="mb-0">
					<li v-for="invoice in skipped_other" :key="invoice.suppliersocr_id">
						{{ invoice.suppliersocr_num }} — {{ $t('invoice.sepa_transfer.skip.' + invoice.reason) }}
					</li>
				</ul>
			</b-alert>

			<b-alert v-if="already_sent.length > 0" show variant="danger">
				{{ $t('invoice.sepa_transfer.ordre_deja_emis') }}
				<ul class="mb-0 mt-2">
					<li v-for="line in already_sent" :key="line.suppliersocr_id">
						{{ line.suppliersocr_num }} — {{ formatDate(line.suppliersocr_sepa_at, true) }}
					</li>
				</ul>
			</b-alert>

			<template v-if="lines.length > 0">
				<table class="table table-sm">
					<thead>
						<tr>
							<th>{{ $t('invoice.sepa_transfer.facture') }}</th>
							<th>{{ $t('invoice.sepa_transfer.fournisseur') }}</th>
							<th>{{ $t('invoice.sepa_transfer.compte_credite') }}</th>
							<th class="text-right">{{ $t('invoice.sepa_transfer.montant') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="line in lines" :key="line.suppliersocr_id">
							<td>{{ line.suppliersocr_num }}</td>
							<td>{{ line.tiers_rs }}</td>
							<td>
								<!-- Une facture qui n'annonce pas son compte est réglée sur la référence du
								     fournisseur : elle peut être portée sur un autre de ses comptes vérifiés,
								     ce que l'écran ne proposait nulle part. -->
								<e-select
									v-if="line.approved_ribs && line.approved_ribs.length > 1"
									:value="line.creditor_rib"
									:options="line.approved_ribs"
									track-by="tiersrib_id"
									label="tiersrib_content"
									:allow-empty="false"
									:disabled="processing"
									@input="onChooseRib(line, $event)"
								/>
								<template v-else>{{ line.creditor_rib.tiersrib_content }}</template>
							</td>
							<td class="text-right">{{ line.balance_format }}</td>
						</tr>
					</tbody>
				</table>

				<p>{{ $t('invoice.sepa_transfer.resume', { count: lines.length, total: total_format }) }}</p>
			</template>
			<b-alert v-else show variant="danger">{{ $t('invoice.sepa_transfer.aucune_facture') }}</b-alert>

			<div class="text-center mt-4">
				<button class="btn btn-primary btn-rounded" :disabled="lines.length === 0" @click="step = 'file'">
					{{ $t('invoice.sepa_transfer.poursuivre') }}
					<font-awesome-icon :icon="['fal', 'angle-right']"/>
				</button>
			</div>
		</template>

		<template v-else>
			<div class="row">
				<div class="col-12 col-sm-6">
					<div class="form-group">
						<label for="checkdeposit">{{ $t('invoice.sepa_transfer.compte_debite') }} *</label>
						<e-select
							id="checkdeposit"
							v-model="debtor_account"
							:options="debtor_accounts"
							track-by="checkdepositconfiguration_id"
							label="account_label"
							:allow-empty="false"
							:disabled="file !== null"
						/>
					</div>
				</div>

				<div class="col-12 col-sm-6">
					<div class="form-group">
						<label for="execution_date">{{ $t('invoice.sepa_transfer.date_execution') }} *</label>
						<e-datepicker id="execution_date" v-model="execution_date" :disabled="file !== null"></e-datepicker>
					</div>
				</div>
			</div>

			<b-alert v-if="debtor_account && !debtor_account.valid_iban" show variant="danger">
				{{ $t('invoice.sepa_transfer.iban_debiteur_invalide') }}
			</b-alert>

			<p>{{ $t('invoice.sepa_transfer.resume', { count: lines.length, total: total_format }) }}</p>

			<div v-if="file === null" class="text-center mt-4">
				<button class="btn btn-primary btn-rounded" :disabled="processing || !generation_ready" @click="onGenerate">
					{{ $t('invoice.sepa_transfer.generer') }}
					<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse/>
					<font-awesome-icon v-else :icon="['fal', 'download']"/>
				</button>
			</div>

			<template v-else>
				<b-alert show variant="success">
					{{ $t('invoice.sepa_transfer.fichier_pret') }}
					<a href="" class="ml-1" @click.prevent="downloadSepaTransfer(file)">{{ file.name }}</a>
				</b-alert>

				<p>{{ $t('invoice.sepa_transfer.confirmation_explication') }}</p>

				<div class="row">
					<div class="col-12 col-sm-6">
						<div class="form-group">
							<label for="payment_date">{{ $t('payment.payment_date') }} *</label>
							<e-datepicker id="payment_date" v-model="payment_date"></e-datepicker>
						</div>
					</div>
				</div>

				<div class="text-center mt-4">
					<button class="btn btn-primary btn-rounded" :disabled="processing" @click="onConfirm">
						{{ $t('invoice.sepa_transfer.confirmer') }}
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse/>
						<font-awesome-icon v-else :icon="['fal', 'check']"/>
					</button>
				</div>
			</template>
		</template>

		<ActionOverlay ref="confirmOverlay" />
	</b-modal>
</template>

<script type="text/javascript">
	import Invoice from "@/mixins/Invoice.js"
	import Tiers from "@/mixins/Tiers.js"

	// Motifs qui tiennent au compte que la facture annonce, et que personne n'a vérifié.
	const IBAN_REASONS = ['iban_quarantine', 'iban_unknown']

	export default {
		name: "InvoiceSupplierSepaTransfer",
		mixins: [Invoice, Tiers],
		data() {
			return {
				loading: false,
				processing: false,
				step: 'check', // 'check' présente ce qui partira et ce qui manque, 'file' produit l'ordre et le confirme.
				invoice_ids: [],
				lines: [],
				skipped: [],
				// Comptes désignés facture par facture, quand celui du fournisseur ne s'impose pas de
				// lui-même. Le choix vaut pour ce virement-ci et ne suit pas la fiche du fournisseur.
				rib_choices: {},
				total_format: '',
				debtor_accounts: [],
				debtor_account: null,
				execution_date: null,
				payment_date: null,
				file: null
			}
		},
		computed: {
			/**
			 * Fournisseurs dont rien ne dit sur quel compte régler : leurs factures n'annoncent aucun
			 * compte, et aucune référence n'a été approuvée sur leur fiche. Le regroupement évite de
			 * répéter le même fournisseur pour chacune de ces factures.
			 *
			 * @return {Array}
			 */
			skipped_no_iban() {
				const tiers = {}

				this.skipped.filter(invoice => invoice.reason === 'no_iban' && invoice.supplier_ribs.length === 0).forEach(invoice => {
					if(!tiers[invoice.tiers_id]) {
						tiers[invoice.tiers_id] = { tiers_id: invoice.tiers_id, tiers_rs: invoice.tiers_rs, invoices: [] }
					}

					tiers[invoice.tiers_id].invoices.push(invoice.suppliersocr_num)
				})

				return Object.values(tiers)
			},
			/**
			 * Factures sans bon à payer. Leur obstacle se lève depuis cet écran, d'où leur bloc propre.
			 *
			 * @return {Array}
			 */
			skipped_not_approved() {
				return this.skipped.filter(invoice => invoice.reason === 'not_approved')
			},
			/**
			 * Factures dont le compte à créditer reste à trancher : compte annoncé en quarantaine ou
			 * jamais rencontré, comptes du fournisseur tous à vérifier, ou plusieurs comptes vérifiés
			 * sans qu'aucun soit désigné. Elles attendent une décision — approuver un compte, ou
			 * désigner l'un de ceux qui l'ont déjà été — et chacune la sienne, un fournisseur pouvant
			 * facturer depuis plusieurs comptes.
			 *
			 * @return {Array}
			 */
			skipped_iban() {
				return this.skipped.filter(invoice => IBAN_REASONS.includes(invoice.reason) || invoice.reason === 'no_default' || (invoice.reason === 'no_iban' && invoice.supplier_ribs.length > 0))
			},
			/**
			 * Factures écartées pour un motif qui ne se corrige pas d'ici : une simple liste suffit.
			 *
			 * @return {Array}
			 */
			skipped_other() {
				return this.skipped.filter(invoice => !['no_iban', 'no_default', 'not_approved'].concat(IBAN_REASONS).includes(invoice.reason))
			},
			/**
			 * Factures pour lesquelles un ordre est déjà parti à la banque sans avoir été confirmé.
			 * En virer une seconde fois enverrait deux fois l'argent, et rien sur la liste ne le dirait.
			 *
			 * @return {Array}
			 */
			already_sent() {
				return this.lines.filter(line => line.suppliersocr_sepa_at)
			},
			/**
			 * Un ordre ne se génère qu'une fois le compte à débiter désigné et sa date d'exécution posée.
			 *
			 * @return {Boolean}
			 */
			generation_ready() {
				return this.debtor_account !== null && this.debtor_account.valid_iban && this.execution_date !== null
			}
		},
		methods: {
			/**
			 * Ouvre la modale sur une sélection de factures et en dresse l'état.
			 *
			 * @param {Array} invoicesupplier_ids
			 */
			async open(invoicesupplier_ids) {
				this.reset()
				this.$refs.modal.show()
				this.loading = true
				// Les factures écartées sortent des lignes retenues : sans la sélection d'origine, un
				// arbitrage rendu sur l'une d'elles ne pourrait plus la ramener dans le virement.
				this.invoice_ids = invoicesupplier_ids

				const check = await this.prepareSepaTransfer(invoicesupplier_ids)

				this.loading = false

				// Sélection refusée d'emblée — plusieurs entités, aucun compte à débiter : le message
				// d'erreur est déjà remonté, la modale n'a plus rien à montrer.
				if(!check) {
					this.close()
					return
				}

				await this.applyCheck(check)

				// Un seul compte : l'utilisateur n'a pas de choix à faire, la question ne lui est posée
				// que lorsqu'elle a une réponse possible.
				this.debtor_account = this.debtor_accounts.length === 1 ? this.debtor_accounts[0] : null
			},
			/**
			 * Reprend l'état de la sélection renvoyé par le serveur.
			 *
			 * @param {Object} check Retour de prepareSepaTransfer ou d'approveSepaTransfer.
			 * @returns {Promise<void>}
			 */
			async applyCheck(check) {
				// Le domaine tient les montants en centimes, priceFormat les attend en unités.
				this.lines = await Promise.all(check.lines.map(async line => ({
					...line,
					balance_format: await this.priceFormat(line.suppliersocr_balance / 100, 'EUR', true)
				})))
				this.skipped = check.skipped
				this.total_format = await this.priceFormat(check.total / 100, 'EUR', true)
				this.debtor_accounts = check.debtor_accounts.map(account => ({
					...account,
					account_label: account.checkdepositconfiguration_libelle + ' - ' + account.checkdepositconfiguration_iban
				}))
			},
			/**
			 * Déclare bonnes à payer les factures qui attendaient ce feu vert, puis reprend la main
			 * avec la sélection à jour : elles rejoignent le virement sans quitter l'écran.
			 *
			 * Les comptes désignés pour d'autres factures partent avec l'appel : l'état renvoyé les
			 * tient, là où un simple recalcul les aurait perdus.
			 *
			 * @returns {Promise<void>}
			 */
			async onApprove() {
				if(this.processing) {
					return
				}

				this.processing = true

				const check = await this.approveSepaTransfer(this.skipped_not_approved.map(invoice => invoice.suppliersocr_id), this.rib_choices)

				this.processing = false

				if(!check) {
					return
				}

				await this.applyCheck(check)
			},
			/**
			 * Comptes du fournisseur d'une facture qui restent à vérifier.
			 *
			 * @param {Object} invoice Facture écartée faute de compte vérifié.
			 * @return {Array}
			 */
			quarantinedRibs(invoice) {
				return invoice.supplier_ribs.filter(rib => rib.tiersrib_status === 'quarantaine')
			},
			/**
			 * Comptes du fournisseur déjà vérifiés, entre lesquels désigner celui qui règlera.
			 *
			 * @param {Object} invoice Facture écartée faute de compte vérifié.
			 * @return {Array}
			 */
			approvedRibs(invoice) {
				return invoice.supplier_ribs.filter(rib => rib.tiersrib_status === 'approuve')
			},
			/**
			 * Fait entrer le compte annoncé par une facture dans ceux du fournisseur.
			 *
			 * L'approbation vaut pour le fournisseur, pas pour la seule facture : c'est la décision de
			 * l'écran de contrôle des factures reçues, prise ici parce que c'est ici qu'elle manque.
			 * Toutes les factures de la sélection qui annoncent ce compte deviennent virables.
			 *
			 * @param {Object} invoice Facture écartée faute de compte vérifié.
			 * @returns {Promise<void>}
			 */
			async onApproveIban(invoice) {
				if(this.processing) {
					return
				}

				this.processing = true

				const saved = await this.saveInvoiceSupplierIban(invoice.suppliersocr_id, 'approuve')

				if(!saved) {
					this.processing = false
					return
				}

				await this.refresh()
				this.processing = false
			},
			/**
			 * Vérifie un compte que la fiche du fournisseur laissait en quarantaine.
			 *
			 * Un fournisseur qui n'avait aucun compte vérifié y gagne sa référence, et ses factures
			 * muettes sur le compte à créditer partent alors sur celui-ci.
			 *
			 * @param {Object} invoice Facture écartée faute de compte vérifié.
			 * @param {Object} rib Compte du fournisseur à approuver.
			 * @returns {Promise<void>}
			 */
			async onApproveRib(invoice, rib) {
				if(this.processing) {
					return
				}

				this.processing = true

				const approved = await this.setTiersRibStatus(invoice.tiers_id, rib.tiersrib_id, 'approuve')

				if(!approved) {
					this.processing = false
					return
				}

				await this.refresh()
				this.processing = false
			},
			/**
			 * Retient un compte déjà vérifié pour régler cette facture-ci.
			 *
			 * Rien n'est écrit sur la fiche du fournisseur : le choix vaut pour ce virement, et la
			 * facture suivante posera de nouveau la question — ce qu'attend un fournisseur encaissant
			 * sur plusieurs comptes.
			 *
			 * @param {Object} invoice Facture écartée faute de compte vérifié, ou retenue sur la
			 *        référence du fournisseur.
			 * @param {Object} rib Compte vérifié désigné pour la régler.
			 * @returns {Promise<void>}
			 */
			async onChooseRib(invoice, rib) {
				if(this.processing) {
					return
				}

				this.processing = true
				this.$set(this.rib_choices, invoice.suppliersocr_id, rib.tiersrib_id)

				await this.refresh()
				this.processing = false
			},
			/**
			 * Redemande l'état de la sélection d'origine, arbitrages compris.
			 *
			 * @returns {Promise<void>}
			 */
			async refresh() {
				const check = await this.prepareSepaTransfer(this.invoice_ids, this.rib_choices)

				if(!check) {
					return
				}

				await this.applyCheck(check)
			},
			close() {
				this.$refs.modal.hide()
			},
			onHidden() {
				this.reset()
			},
			reset() {
				this.step = 'check'
				this.loading = false
				this.processing = false
				this.invoice_ids = []
				this.lines = []
				this.skipped = []
				this.rib_choices = {}
				this.total_format = ''
				this.debtor_accounts = []
				this.debtor_account = null
				this.file = null

				// Une banque refuse un ordre daté du jour même passé l'heure de coupure : le lendemain
				// est la première date qu'elle acceptera à coup sûr.
				const tomorrow = new Date()
				tomorrow.setDate(tomorrow.getDate() + 1)
				this.execution_date = tomorrow
				this.payment_date = tomorrow
			},
			/**
			 * Produit le fichier de virement et le remet aussitôt à l'utilisateur.
			 *
			 * @returns {Promise<void>}
			 */
			async onGenerate() {
				if(this.processing) {
					return
				}

				this.processing = true

				const file = await this.generateSepaTransfer(
					this.lines.map(line => line.suppliersocr_id),
					this.debtor_account.checkdepositconfiguration_id,
					this.execution_date.toDateInputValue(),
					this.rib_choices
				)

				this.processing = false

				if(!file) {
					return
				}

				this.file = file
				this.downloadSepaTransfer(file)
			},
			/**
			 * Enregistre les règlements des factures dont l'ordre est parti à la banque.
			 *
			 * @returns {Promise<void>}
			 */
			async onConfirm() {
				if(this.processing) {
					return
				}

				this.processing = true
				this.$refs.confirmOverlay.show(this.$t('global.enregistrement_en_cours'))

				const payments = await this.confirmSepaTransfer(
					this.lines.map(line => line.suppliersocr_id),
					this.debtor_account.checkdepositconfiguration_id,
					this.payment_date.toDateInputValue(),
					this.file.generated_at
				)

				if(!payments) {
					this.$refs.confirmOverlay.hide()
					this.processing = false
					return
				}

				await this.$refs.confirmOverlay.showSuccess(this.$t('global.enregistrement_succes'))
				this.processing = false
				this.$emit('submit')
				this.close()
			}
		},
		components: {
			ActionOverlay: () => import('@/components/Utils/ActionOverlay'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
	}
</script>
